/* global console */

import { CoObjectItem } from "../interfaces/CoObject";
import React, { ReactNode, useEffect } from "react";
import { extractSelectedContentControl } from "../../office/DocumentManager";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { OperationCompletedPanel } from "../layout/OperationCompletedPanel";
import { CoObjectTypeEnum } from "../../api/models";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { isSubPartContentControl } from "../../office/ContentControl";
import { CustomModal, CustomModalProps } from "../layout/ActionModal";
import LoadingManager from "../../common/loading/LoadingManager";
import { SELECT_COOBJECTS_NOTIFICATION } from "../../utils/constants/notification";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import FooterPanel from "../layout/FooterPanel";
import {
  deleteContentControlById,
  getCoObjectFromTagOrCustomXmlByContentControlId,
} from "../../utils/helpers/ContentControlHelper";
import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import { allInheritedHasValue, applyInheritedInfo } from "../../utils/helpers/InheritanceHelper";
import {
  applyComponentDataToLegacyComponentInstance,
  isLegacyComponentInstance,
} from "../../utils/helpers/ComponentInstanceHelper";
import NotificationManager from "../../common/notification/NotificationManager";

export interface CoObjectSelectorProps {
  children: (coObject: CoObjectItem) => ReactNode;
  documentPartMetadata: DocumentPartMetadata;
}

interface CoObjectSelectorState {
  failed: boolean;
  contentControlId?: number;
  coObject?: CoObjectItem;
  isContentControlDeletable: boolean;
  deleteConfirm: boolean;
  deleted: boolean;
}

const coObjectSelectorInitialState = {
  failed: false,
  isContentControlDeletable: false,
  deleteConfirm: false,
  deleted: false,
};

export const CoObjectSelector: React.FunctionComponent<CoObjectSelectorProps> = (props) => {
  const [state, setState] = React.useState<CoObjectSelectorState>(coObjectSelectorInitialState);
  const { documentPartMetadata } = props;
  const { failed, coObject, isContentControlDeletable, deleteConfirm, contentControlId, deleted } = state;
  const deleteConfirmModalProps = (contentControlId: number): CustomModalProps => {
    return {
      modalTitle: formatMessageWithParams("cof.createnewobject.modal.delete", {
        coObjectName: "",
      }),
      messageTitle: "Are you sure you want to Delete document content?",
      message: "Are you sure you want to Delete document content?",
      type: "delete",
      onHide: () => {
        return;
      },
      actions: [
        {
          title: formatMessage("cof.base.noconfirmdelete"),
          onClick: () => {
            setState({ ...state, deleteConfirm: false });
          },
          variant: "blue",
        },
        {
          title: formatMessage("cof.base.confirmdelete"),
          onClick: async () => {
            await deleteContentControlById(contentControlId);
            setState({
              ...state,
              deleteConfirm: false,
              deleted: true,
              isContentControlDeletable: false,
              contentControlId: undefined,
            });
          },
          variant: "red_filled",
        },
      ],
    };
  };

  useEffect(() => {
    void fetchObject(documentPartMetadata);
  }, []);

  const fetchObject = async (documentPartMetadata: DocumentPartMetadata) => {
    try {
      LoadingManager.create(SELECT_COOBJECTS_NOTIFICATION);
      const contentControlId = await extractSelectedContentControl();
      if (contentControlId) {
        const isDeletable = !(await isSubPartContentControl(contentControlId));
        await getObjectFromTag(contentControlId, isDeletable, documentPartMetadata);
      } else {
        setState((prevState) => ({
          ...prevState,
          failed: true,
        }));
      }
    } catch (error) {
      console.log("error contentControlId ", error);
      setState((prevState) => ({
        ...prevState,
        failed: true,
      }));
    } finally {
      LoadingManager.remove(SELECT_COOBJECTS_NOTIFICATION);
    }
  };

  const getObjectFromTag = async (
    contentControlId: number,
    isDeletable: boolean,
    documentPartMetadata: DocumentPartMetadata,
  ) => {
    try {
      console.log("fetching object from TAG");
      let coObject = await getCoObjectFromTagOrCustomXmlByContentControlId(contentControlId);
      if (coObject != null) {
        if (isLegacyComponentInstance(coObject)) {
          coObject = await applyComponentDataToLegacyComponentInstance(coObject, documentPartMetadata);
        } else {
          await applyInheritedInfo(coObject, documentPartMetadata);
        }
        if (!allInheritedHasValue(coObject))
          NotificationManager.addNotification({
            key: "inheritparameters.error",
            title: "",
            message: formatMessage("cof.createnewobject.inheritparameters.error"),
            type: "warning",
            isTemporary: false,
          });

        setState((prevState) => ({
          ...prevState,
          coObject: coObject,
          contentControlId: contentControlId,
        }));
      } else {
        console.log("error getting xml ");
        setState((prevState) => ({
          ...prevState,
          failed: true,
          isContentControlDeletable: isDeletable,
          contentControlId: contentControlId,
        }));
      }
    } catch (error) {
      console.log("error getting xml ", error);
      setState((prevState) => ({
        ...prevState,
        failed: true,
        isContentControlDeletable: isDeletable,
        contentControlId: contentControlId,
      }));
    }
  };

  return (
    <>
      {deleteConfirm && contentControlId !== null && <CustomModal {...deleteConfirmModalProps(contentControlId)} />}
      {!coObject && !failed ? null : (
        <>
          {coObject && coObject.info.objectType != CoObjectTypeEnum.DOCUMENT_PART_INCLUDE ? (
            <>{props.children(coObject)}</>
          ) : (
            <>
              {isContentControlDeletable && !coObject ? (
                <OperationCompletedPanel
                  title={formatMessage("cof.base.edit")}
                  message={formatMessage("cof.base.confirmation.deleteinvalidobject")}
                  alt={formatMessage("cof.base.confirmation.deleteinvalidobject")}
                  icon="/assets/noObject.svg"
                >
                  <FooterPanel>
                    <PrimaryButton
                      id={"delete"}
                      text={formatMessage("cof.base.delete").toUpperCase()}
                      onClick={() => setState({ ...state, deleteConfirm: true })}
                      className={ButtonVariants.red_filled}
                      style={{ float: "left" }}
                      data-testid="deletecc"
                    />
                  </FooterPanel>
                </OperationCompletedPanel>
              ) : (
                <>
                  {deleted ? (
                    <OperationCompletedPanel
                      message={formatMessage("cof.createnewobject.modal.successmessage")}
                      alt={formatMessage("cof.createnewobject.modal.successmessage")}
                      icon="/assets/trash-3x.png"
                    />
                  ) : (
                    <OperationCompletedPanel
                      title={formatMessage("cof.base.edit")}
                      message={formatMessage("cof.objects.edit.message.anyfindeditableobject")}
                      alt={formatMessage("cof.objects.edit.message.anyfindeditableobject")}
                      icon="/assets/noObject.svg"
                    />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
